import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import ProductCard from '../components/ProductCard';
import ProductModal from '../components/ProductModal';
import CatalogoColores from '../components/CatalogoColores';
import {
  CATALOGO_UNAS_LOCAL,
  CATALOGO_UNAS_SUBTITLE,
  CATALOGO_UNAS_TITLE,
  MANICURE_CATEGORY,
  UNAS_LINEAS,
  countByUnasLinea,
  matchUnasLinea,
} from '../config/unasCatalog';

async function fetchAllUnas() {
  const pageSize = 100;
  let offset = 0;
  let total = Infinity;
  const all = [];
  while (offset < total) {
    const r = await api.getProductos({
      subcategoria: MANICURE_CATEGORY,
      limit: pageSize,
      offset,
    });
    const chunk = r.data || [];
    total = Number(r.total) || chunk.length;
    all.push(...chunk);
    offset += chunk.length;
    if (!chunk.length) break;
  }
  return all;
}

export default function CatalogoUnas({ onAdd, onToast }) {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linea, setLinea] = useState('todas');
  const [view, setView] = useState('colores'); // colores | productos
  const [preview, setPreview] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAllUnas()
      .then((data) => {
        if (!cancelled) setProductos(data);
      })
      .catch(() => {
        if (!cancelled) setProductos([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const counts = useMemo(() => countByUnasLinea(productos), [productos]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return productos.filter((p) => {
      if (!matchUnasLinea(p, linea)) return false;
      if (!term) return true;
      const hay = `${p.codigo || ''} ${p.nombre || ''} ${p.referencia || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [productos, linea, q]);

  const activeLinea = UNAS_LINEAS.find((l) => l.id === linea) || UNAS_LINEAS[0];

  return (
    <div className="unas-page">
      <section className="unas-hero">
        <div className="unas-hero-inner">
          <p className="section-kicker">Catálogo oficial</p>
          <h1>{CATALOGO_UNAS_TITLE}</h1>
          <p>{CATALOGO_UNAS_SUBTITLE}</p>
          <div className="unas-hero-actions">
            <button
              type="button"
              className={`btn-primary ${view === 'colores' ? 'ghost-on' : ''}`}
              onClick={() => setView('colores')}
            >
              Colores
            </button>
            <button
              type="button"
              className={`btn-ghost ${view === 'productos' ? 'ghost-on' : ''}`}
              onClick={() => setView('productos')}
            >
              Ver productos
            </button>
            <a className="btn-ghost" href={CATALOGO_UNAS_LOCAL} target="_blank" rel="noreferrer">
              Abrir PDF
            </a>
            <Link className="btn-ghost" to={`/catalogo?cat=${encodeURIComponent(MANICURE_CATEGORY)}`}>
              Ir a catálogo Uñas
            </Link>
          </div>
        </div>
      </section>

      {view === 'productos' ? (
      <section className="unas-lineas section">
        <div className="unas-lineas-scroll">
          {UNAS_LINEAS.map((l) => (
            <button
              key={l.id}
              type="button"
              className={`unas-linea-chip ${linea === l.id ? 'active' : ''}`}
              onClick={() => {
                setLinea(l.id);
                setView('productos');
              }}
            >
              <strong>{l.nombre}</strong>
              <em>{counts[l.id] ?? 0}</em>
            </button>
          ))}
        </div>
        <p className="unas-linea-blurb">{activeLinea.blurb}</p>
      </section>
      ) : null}

      {view === 'colores' ? (
        <CatalogoColores />
      ) : (
        <section className="unas-shop section">
          <div className="unas-shop-toolbar">
            <input
              type="search"
              placeholder="Buscar en uñas (nombre o código)…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <span>
              {loading ? 'Cargando…' : `${filtered.length} productos · ${activeLinea.nombre}`}
            </span>
          </div>
          <div className="shop-grid">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="shop-card skeleton">
                    <div className="skeleton-img" />
                    <div className="skeleton-line" />
                    <div className="skeleton-line short" />
                  </div>
                ))
              : filtered.map((p) => (
                  <ProductCard
                    key={p.codigo}
                    product={p}
                    onPreview={setPreview}
                    onAdd={(prod) => {
                      onAdd(prod);
                    }}
                  />
                ))}
          </div>
          {!loading && !filtered.length ? (
            <p className="empty">No hay productos en esta línea. Prueba otra o abre el PDF.</p>
          ) : null}
        </section>
      )}

      {preview ? (
        <ProductModal
          product={preview}
          products={filtered}
          onSelect={setPreview}
          onClose={() => setPreview(null)}
          onAdd={(prod, opts) => {
            onAdd(prod, opts);
          }}
        />
      ) : null}
    </div>
  );
}
